import React, { useEffect, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import Button from "../../components/common/Button.tsx";
import { grey } from "@mui/material/colors";
import { useNavigate, useLocation } from "react-router-dom";
import { DownloadTemplate, handleForecastInference } from "../../modules/predictorModule.ts";
import {toast, ToastContainer} from 'react-toastify';import { loadPredictionDataset } from "./helper.ts";
import ErrorModal from "../../components/common/ErrorModal.tsx";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { CardBody, Col, Input, Row } from "reactstrap";
import { LabelText } from "../../components/common/Labeltext.tsx";
import SelectField from "../../components/common/SelectField.tsx";

interface Dataset {
  id: number;
  file_name: string;
  dataset_name: string;
  created_at: string;
  modified_at: string;
  total_fields: number;
  total_records: number;
  created_by: number;
}

const FormForecast = () => {
  const navigate = useNavigate();
  const location = useLocation();
  //const toast = useToast();

  const [uniqueId, setUniqueId] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [duration, setDuration] = useState<string | null>(null);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<any>(datasets[0])
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [forecastData, setForecastData] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false)
  const train_id = location.state.predictor.id;
  const preprocess_id = location.state.predictor.preprocess_id;
  const durationOptions = ["Minutes", "Hours", "Days", "Weeks", "Months", "Years"];

  const handleCancel = () => {
    navigate("/TrainingModal");
  };

  useEffect(() => {
    loadPredictionDataset(setIsLoadingDatasets, setDatasets, location.state.predictor.dataset_id);
  }, []);

  const handleForecast = async () => {
    if (uniqueId && inputValue && duration) {
      setIsSubmitting(true);
      try {
        const steps = parseInt(inputValue, 10);
        const frequencyMap: { [key: string]: string } = {
          Minutes: "min",
          Hours: "hour",
          Days: "Day",
          Weeks: "Week",
          Months: "Month",
          Years: "Year",
        };
        const frequency = frequencyMap[duration];
        const response = await handleForecastInference(
          train_id,
          preprocess_id,
          steps,
          frequency,
          uniqueId,
          selectedDataset ? selectedDataset.id : 0,
          location?.state?.predictor?.used_exogenous
        );
        setForecastData(response);
        navigate(`/forecast-result/${location.state.predictor.id}`, {
          state: {
            data: response,
            predictor_name: location?.state?.predictor?.predictor_name
          },
        });
        setShowResults(true);
        toast.success("Forecast generated successfully.");
      } catch (error: any) {
        let errorMessage = "Failed to generate forecast. Please try again.";
        if (error?.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        setMessage(errorMessage);
        setIsOpen(true)
        // toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      toast.error("Please enter unique ID, value and select a duration.");
    }
  };

  const downloadTemplate = async () => {
    if (!train_id) {
      toast.error("Invalid train ID");
      return;
    }
    try {
      setIsLoading(true);
      const response = await DownloadTemplate(parseInt(train_id));
      const csvHeaders = ["column_name", "data_type", "default_value"];
      const csvRows = response?.template_schema?.map((row) => {
        return Object.values(row).join(',');
      });
      const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", "Prediction_template.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      const errorMessage = error?.response?.data?.detail || "Failed to fetch recent batch prediction";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <BreadCrumb moduleName={"Training"} pageName={"Forecast"} viewName={location?.state?.predictor?.predictor_name} />
      <ToastContainer position="top-center" />
      <div className="listingjs-table " id="customerList">
        <div className="live-preview">
          <div className="align-items-center d-flex mb-3 ">
            <h4 className="card-title mb-0 flex-grow-1">Form Based Forecast</h4>
          </div>
          <div className="border table-border p-3">
            <div className=''>
              <CardBody>
                <Row className="gy-3 pt-1 pb-2 ">
                  <Col lg={12}>
                    <LabelText
                      htmlFor={"UniqueID"}
                      className={"text-danger"}
                      displayText={"Unique ID"}
                      important={"*"}
                    />
                    <LabelText
                      htmlFor={"UniqueIDPlaceholder"}
                      classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                      displayText={"Enter a Unique ID from the Dataset"}
                    />
                    <Input
                      type="text"
                      value={uniqueId}
                      onChange={(e) => setUniqueId(e.target.value)}
                    />
                  </Col>
                  <Row className="mt-3 me-0 pe-0 w-100">
                    <LabelText
                      htmlFor={"inputValue"}
                      className={"text-danger"}
                      displayText={"Forecast Duration"}
                      important={"*"}
                    />
                    <Col lg={6}>
                      <LabelText
                        htmlFor={"inputValuePlaceholder"}
                        classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                        displayText={"Enter a Number"}
                      />
                      <Input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                      />
                    </Col>
                    <Col lg={6}>
                      <LabelText
                        htmlFor={"DurationPlaceholder"}
                        classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                        displayText={"Select a Duration"}
                      />
                      <SelectField
                        className={"w-100"}
                        value={
                          duration
                            ? { label: duration, value: duration }
                            : null
                        }
                        placeholder={" "}
                        handleChange={(newValue) => {
                          if (newValue) setDuration(newValue.value)
                        }}
                        options={
                          durationOptions.map(opt => ({
                            label: opt,
                            value: opt
                          }))}
                        isRVStyle={true}
                      />
                    </Col>
                  </Row>
                  {location?.state?.predictor?.used_exogenous && <Col lg={12}>
                    <LabelText
                      htmlFor={"Dataset"}
                      className={"text-danger"}
                      displayText={"Choose Dataset for Additional Inputs"}
                      important={"*"}
                    />
                    <div className="d-flex justify-content-between align-items-start">
                      <LabelText
                        htmlFor={"DatasetPlaceholder"}
                        classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                        displayText={"Select a dataset"}
                      />
                      <span
                        className="text-primary cursor-pointer text-decoration-underline"
                        onClick={downloadTemplate}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") downloadTemplate();
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        Download template
                      </span>
                    </div>
                    <SelectField
                      value={
                        selectedDataset
                          ? { label: selectedDataset.dataset_name, value: selectedDataset.id }
                          : null
                      }
                      placeholder={" "}
                      handleChange={(newValue) => {
                        if (newValue) {
                          const selected = datasets.find(d => d.id === newValue.value);
                          setSelectedDataset(selected || null);
                        } else {
                          setSelectedDataset(datasets[0]);
                        }
                      }}
                      showLoadingIndicator={isLoadingDatasets}
                      options={
                        datasets.map(opt => ({
                          label: opt.dataset_name,
                          value: opt.id
                        }))}
                      isRVStyle={true}
                    />
                  </Col>}
                </Row>
                <Row lg={6} className=" justify-content-center mb-0 mt-4">
                  <div className="live-preview">
                    <div className="d-flex justify-content-center gap-2">
                      <button
                        onClick={handleForecast}
                        disabled={isSubmitting}
                        type="submit"
                        className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary"
                      >
                        <i className="ri-play-fill label-icon align-middle fs-16 me-1"></i>
                        {isSubmitting ? (
                          <div className="flex items-center space-x-2 ms-4">
                            <span>Please wait</span>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : (
                          <span className="ms-4 me-2">Forecast</span>
                        )}
                      </button>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </div>
          </div>
          <ErrorModal open={isOpen} onClose={() => setIsOpen(false)} message={message} buttonText="Okay!" />
        </div>
      </div >
    </>
  );
};

export default FormForecast;